import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "../Css/AdminDashboard.css";
import "../Css/DispatcherDashboard.css";
import { FiShoppingCart, FiPackage, FiTruck, FiCheckCircle, FiXCircle, FiUsers, FiClock, FiAlertTriangle, FiUserX } from "react-icons/fi";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip as LeafletTooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Navbar from './Navbar';
import "../Css/leaflet-fix.css";
import L from "leaflet";
import Logger from "./Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

// Initialize Logger
const logger = new Logger(getLoggerBaseURL());

/** Animated shimmer skeleton block */
const Skeleton = ({ width = "100%", height = 20, radius = 6, style = {} }) => (
    <div style={{
        width,
        height,
        borderRadius: radius,
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "skeletonShimmer 1.4s infinite",
        ...style
    }} />
);

/** Thin top progress bar */
const TopProgressBar = ({ visible }) => (
    <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        height: 3,
        background: "transparent",
        pointerEvents: "none",
    }}>
        <div style={{
            height: "100%",
            width: visible ? "85%" : "100%",
            background: "linear-gradient(90deg, #3b82f6, #6366f1)",
            transition: visible
                ? "width 0.4s ease"
                : "width 0.3s ease, opacity 0.4s ease 0.1s",
            opacity: visible ? 1 : 0,
            borderRadius: "0 2px 2px 0",
            boxShadow: "0 0 8px #3b82f680",
        }} />
    </div>
);

/** Inline spinner */
const Spinner = ({ size = 20, color = "#3b82f6" }) => (
    <div style={{
        width: size,
        height: size,
        border: `2px solid ${color}22`,
        borderTop: `2px solid ${color}`,
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
        flexShrink: 0,
    }} />
);

/** Section loader */
const SectionLoader = ({ height = 120, label = "Loading…" }) => (
    <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height,
        gap: 10,
        color: "#94a3b8",
        fontSize: 13,
    }}>
        <Spinner />
        <span>{label}</span>
    </div>
);

const KPISkeletonTile = () => (
    <div className="kpi-tile kpi-blue" style={{ gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Skeleton width={90} height={14} />
            <Skeleton width={28} height={28} radius={8} />
        </div>

        <Skeleton width={60} height={32} radius={6} />
        <Skeleton width={120} height={12} />
    </div>
);

// ─── GPS / OSRM constants ─────────────────────────────────────────────────────

// OSRM /route config  (NOT /match — route always follows real roads)
const OSRM_WAYPOINTS_PER_CALL = 50;  // route API supports up to 100; 50 is safe
const OSRM_MAX_WAYPOINTS = 40;  // thin GPS down to this before routing

// GPS cleaning thresholds
const MIN_MOVE_KM = 0.005;  // ignore jitter under 5 m
const MAX_JUMP_KM = 0.50;   // ignore teleports over 500 m
const MAX_SPEED_KH = 120;    // ignore points implying > 120 km/h
const MAX_SPIKE_KM = 0.15;   // spike leg must be under 150 m to be considered noise

// ─── Geometry utilities (module-level, no re-creation on render) ──────────────

const toRad = (deg) => (deg * Math.PI) / 180;

/** Haversine distance in km. */
const distKm = (a, b) => {
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/**
 * Remove GPS noise from a chronologically sorted point list.
 *
 * Filters applied in order:
 *   1. Sub-5 m jitter                         → skip
 *   2. > 500 m jump                           → skip (teleport / GPS glitch)
 *   3. Implied speed > 120 km/h               → skip
 *   4. Spike / triangle pattern (look-ahead)  → skip
 *      curr is a noise spike when:
 *        - prev→curr and curr→next are both short (< 150 m each)
 *        - but the detour ratio is high: (d1 + d2) > 2.5 × direct prev→next
 *      This catches the "arrow" artifacts visible in the GPS trace.
 */
const cleanGPSPoints = (points) => {
    if (!points?.length) return [];

    const valid = points
        .map((p) => ({
            lat: Number(p.lat),
            lng: Number(p.lng),
            ts: p.timestamp ? new Date(p.timestamp).getTime() : null,
        }))
        .filter((p) => !isNaN(p.lat) && !isNaN(p.lng));

    if (valid.length < 2) return valid;

    const out = [valid[0]];

    for (let i = 1; i < valid.length; i++) {
        const prev = out[out.length - 1];
        const curr = valid[i];
        const d1 = distKm(prev, curr);

        // 1. Jitter
        if (d1 < MIN_MOVE_KM) continue;

        // 2. Teleport
        if (d1 > MAX_JUMP_KM) continue;

        // 3. Speed check (only when both timestamps exist)
        if (curr.ts && prev.ts) {
            const hours = (curr.ts - prev.ts) / 3_600_000;
            if (hours > 0 && d1 / hours > MAX_SPEED_KH) continue;
        }

        // 4. Spike / triangle look-ahead
        if (i < valid.length - 1) {
            const next = valid[i + 1];
            const d2 = distKm(curr, next);
            const d3 = distKm(prev, next);
            // curr is a detour spike: both legs short but combined >> direct distance
            if (d1 < MAX_SPIKE_KM && d2 < MAX_SPIKE_KM && (d1 + d2) > d3 * 2.5) {
                continue;
            }
        }

        out.push(curr);
    }

    return out;
};

/** Thin point list to at most maxPts while keeping first & last. */
const subsamplePoints = (points, maxPts = OSRM_MAX_WAYPOINTS) => {
    if (points.length <= maxPts) return points;
    const step = Math.ceil(points.length / maxPts);
    return points.filter((_, i) => i % step === 0 || i === points.length - 1);
};

/**
 * Route a cleaned point list along actual roads using OSRM /route.
 *
 * WHY /route INSTEAD OF /match:
 *   /match tries to snap a dense GPS trace to roads — it needs clean,
 *   high-frequency data to work. With noisy GPS it draws straight lines
 *   through buildings when it fails.
 *
 *   /route takes a list of waypoints and finds the real road path between
 *   them. It ALWAYS follows streets, regardless of GPS quality.
 */
const fetchSnappedRoute = async (points) => {
    if (points.length < 2) return points.map((p) => [p.lat, p.lng]);

    // Thin GPS down to a manageable set of waypoints
    const waypoints = subsamplePoints(points, OSRM_MAX_WAYPOINTS);

    // Build chunks with 1-point overlap for seamless joining
    const chunks = [];
    for (let i = 0; i < waypoints.length; i += OSRM_WAYPOINTS_PER_CALL - 1) {
        const chunk = waypoints.slice(i, i + OSRM_WAYPOINTS_PER_CALL);
        if (chunk.length >= 2) chunks.push(chunk);
    }

    const finalPath = [];

    for (const chunk of chunks) {
        try {
            const coords = chunk.map((p) => `${p.lng},${p.lat}`).join(";");

            const res = await fetch(
                `https://router.project-osrm.org/route/v1/driving/${coords}` +
                `?overview=full&geometries=geojson&continue_straight=false`
            );
            const data = await res.json();

            if (!data.routes?.length) continue;

            const routed = data.routes[0].geometry.coordinates.map(
                ([lng, lat]) => [lat, lng]
            );

            // Drop first point on subsequent chunks — shared with previous chunk's last
            finalPath.push(...(finalPath.length ? routed.slice(1) : routed));
        } catch (err) {
            console.warn("OSRM route chunk failed:", err.message);
        }
    }

    // Last-resort fallback only if OSRM is completely unreachable
    return finalPath.length ? finalPath : points.map((p) => [p.lat, p.lng]);
};

if (typeof document !== "undefined" && !document.getElementById("dd-keyframes")) {
    const s = document.createElement("style");
    s.id = "dd-keyframes";

    s.textContent = `
        @keyframes skeletonShimmer {
            0%   { background-position: 200% 0; }
            100% { background-position: -200% 0; }
        }

        @keyframes spin {
            to { transform: rotate(360deg); }
        }
    `;

    document.head.appendChild(s);
}

// ─── Component ────────────────────────────────────────────────────────────────

const AdminDashboard = () => {
    const [tableDriver, setTableDriver] = useState([]);
    const [tableFailedOrder, setTableFailedOrder] = useState({ failedOrdersByDriver: [] });
    const [tableCompletedOrder, setTableCompletedOrder] = useState([]);
    const [tableCollectedPOD, setTableCollectedPOD] = useState([]);
    const [dateRange, setDateRange] = useState("today");
    const userEmail = sessionStorage.getItem("userEmail");
    const [liveStats, setLiveStats] = useState({ total: 0, unassigned: 0, inProgress: 0, completed: 0 });
    const [trendData, setTrendData] = useState([]);
    const [overviewStats, setOverviewStats] = useState({ total: 0, unassigned: 0, inProgress: 0, completed: 0, failed: 0 });
    const [showAllDrivers, setShowAllDrivers] = useState(false);
    const [orders, setOrders] = useState([]);
    const [allDriverTracks, setAllDriverTracks] = useState([]);
    const [showDriverOrdersModal, setShowDriverOrdersModal] = useState(false);
    const [selectedDriverForModal, setSelectedDriverForModal] = useState(null);
    const [driverModalOrders, setDriverModalOrders] = useState([]);
    const [driverModalLoading, setDriverModalLoading] = useState(false);
    const [processedDriverTracks, setProcessedDriverTracks] = useState([]);
    const [showData, setShowData] = useState(true);
    const [kpiLoading, setKpiLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [driversLoading, setDriversLoading] = useState(true);
    const [trackingLoading, setTrackingLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);

    const routeColors = [
        "#e63946", "#1d3557", "#2a9d8f",
        "#f4a261", "#6a4c93", "#ff006e", "#3a86ff",
        "#8338ec", "#fb5607", "#43aa8b",
    ];

    const ICONS = {
        BIKE: "https://cdn-icons-png.flaticon.com/512/2972/2972185.png",
        "E-BIKE": "https://cdn-icons-png.flaticon.com/512/2972/2972185.png",
        CAR: "https://cdn-icons-png.flaticon.com/512/744/744465.png",
        DEFAULT: "https://cdn-icons-png.flaticon.com/512/1946/1946429.png",
    };

    const getDriverIcon = (type, color = "#e63946") => {
        const iconUrl = ICONS[type] || ICONS.DEFAULT;
        return L.divIcon({
            className: "",
            html: `
                <div style="position:relative;width:42px;height:42px;">
                    <div style="
                        position:absolute;top:50%;left:50%;
                        transform:translate(-50%,-50%);
                        width:42px;height:42px;border-radius:50%;
                        background:${color}22;border:2px solid ${color};
                        animation:driverPulse 1.8s ease-out infinite;
                    "></div>
                    <div style="
                        position:absolute;top:50%;left:50%;
                        transform:translate(-50%,-50%);
                        width:30px;height:30px;border-radius:50%;
                        background:white;border:2.5px solid ${color};
                        display:flex;align-items:center;justify-content:center;
                        box-shadow:0 2px 8px rgba(0,0,0,0.25);overflow:hidden;
                    ">
                        <img src="${iconUrl}" width="18" height="18" style="object-fit:contain;" />
                    </div>
                </div>`,
            iconSize: [42, 42],
            iconAnchor: [21, 21],
        });
    };

    const getStartIcon = (color = "#10b981") =>
        L.divIcon({
            className: "",
            html: `<div style="
                width:14px;height:14px;border-radius:50%;
                background:${color};border:2.5px solid white;
                box-shadow:0 2px 6px rgba(0,0,0,0.3);
            "></div>`,
            iconSize: [14, 14],
            iconAnchor: [7, 7],
        });

    if (typeof document !== "undefined" && !document.getElementById("driver-pulse-style")) {
        const style = document.createElement("style");
        style.id = "driver-pulse-style";
        style.textContent = `@keyframes driverPulse {
            0%   { transform:translate(-50%,-50%) scale(1);   opacity:0.8; }
            100% { transform:translate(-50%,-50%) scale(2.2); opacity:0; }
        }`;
        document.head.appendChild(style);
    }

    // ── Process driver tracks whenever raw data changes ───────────────────────
    useEffect(() => {
        if (!allDriverTracks?.length) {
            setProcessedDriverTracks([]);
            return;
        }
        let cancelled = false;

        const processAll = async () => {
            const results = await Promise.all(
                allDriverTracks.map(async (driver) => {
                    const cleaned = cleanGPSPoints(driver.route || []);
                    if (cleaned.length < 2) {
                        return { ...driver, snappedPath: cleaned.map((p) => [p.lat, p.lng]) };
                    }
                    const snappedPath = await fetchSnappedRoute(cleaned);
                    return { ...driver, snappedPath };
                })
            );
            if (!cancelled) setProcessedDriverTracks(results);
        };

        processAll();
        return () => { cancelled = true; };
    }, [allDriverTracks]);

    const FitBounds = ({ tracks }) => {
        const map = useMap();
        useEffect(() => {
            const allPoints = tracks.flatMap((d) => d.snappedPath || []);
            if (allPoints.length > 0) map.fitBounds(allPoints, { padding: [50, 50], maxZoom: 14 });
        }, [tracks, map]);
        return null;
    };

    const tableFailedOrdersMap = React.useMemo(() => new Map(
        (tableFailedOrder.failedOrdersByDriver || []).map((item) => [item._id, item.count || 0])
    ), [tableFailedOrder]);

    const tableCompletedOrdersMap = React.useMemo(() => new Map(
        (tableCompletedOrder || []).map((item) => [item._id, item.totalCompletedOrders || 0])
    ), [tableCompletedOrder]);

    const tableCollectedPODMap = React.useMemo(() => new Map(
        (tableCollectedPOD || []).map((item) => [item._id, item.collectedPOD || 0])
    ), [tableCollectedPOD]);

    const fetchData = useCallback(async () => {
        setChartLoading(true);
        try {
            const trendResponse = await fetch(`/api/dashboard/trend/${userEmail}?range=${dateRange}`);
            if (!trendResponse.ok) { setTrendData([]); return; }
            const trend = await trendResponse.json();
            setTrendData(trend || []);
        } catch (err) {
            logger.log(`Fetch error: ${err}`, "ERROR");
        } finally {
            setChartLoading(false);
        }
    }, [userEmail, dateRange]);

    const fetchKPIStats = useCallback(async () => {
        setKpiLoading(true);
        try {
            const res = await fetch(`/api/dashboard/kpi/${userEmail}`);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const data = await res.json();
            setLiveStats({
                total: data.total || 0,
                unassigned: data.unassigned || 0,
                inProgress: data.inProgress || 0,
                completed: data.completed || 0,
                failed: data.failed || 0,
                billsForCollection: data.billsForCollection || 0,
                totalDueAmountForCollection: data.totalDueAmountForCollection || 0,
                activeDrivers: data.activeDrivers || 0,
                totalDrivers: data.totalDrivers || 0,
                today: data.today || {},
                yesterday: data.yesterday || {}
            });
        } catch (err) {
            logger.log(`KPI fetch error: ${err}`, "ERROR");
        } finally {
            setKpiLoading(false);
        }
    }, [userEmail]);

    const fetchOverviewStats = useCallback(async () => {
        try {
            const res = await fetch(`/api/dashboard/order-overview/${userEmail}?selectedDate=${dateRange}`);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const data = await res.json();
            setOverviewStats(data ?? { total: 0, unassigned: 0, inProgress: 0, completed: 0, failed: 0 });
        } catch (err) {
            logger.log(`Overview stats error: ${err}`, "ERROR");
        }
    }, [userEmail, dateRange]);

    const fetchTableData = useCallback(async () => {
        setDriversLoading(true);
        try {
            const formattedDate = "";
            const [
                driverTableResponse,
                failedOrderTableResponse,
                CollectedPODTableResponse,
                completedOrderTableResponse
            ] = await Promise.all([
                fetch(`/api/dispatches/${userEmail}`),
                fetch(`/api/dashboard/failed-orders/${userEmail}?selectedDate=${formattedDate}`),
                fetch(`/api/dashboard/todaysCollectionPOD/${userEmail}?selectedDate=${formattedDate}`),
                fetch(`/api/dashboard/completedOrdersOfDriver/${userEmail}?selectedDate=${formattedDate}`),
            ]);

            const tableDriverData = await driverTableResponse.json();
            const tableFailedData = await failedOrderTableResponse.json();
            const tableCollectedPODData = await CollectedPODTableResponse.json();
            const tableCompletedData = await completedOrderTableResponse.json();

            setTableDriver(Array.isArray(tableDriverData) ? tableDriverData : []);
            setTableFailedOrder(
                tableFailedData && typeof tableFailedData === "object"
                    ? tableFailedData
                    : { failedOrdersByDriver: [] },
            );
            setTableCompletedOrder(
                Array.isArray(tableCompletedData?.completedOrdersByDriver)
                    ? tableCompletedData.completedOrdersByDriver
                    : [],
            );
            setTableCollectedPOD(
                Array.isArray(tableCollectedPODData?.collectedPODByDriver)
                    ? tableCollectedPODData.collectedPODByDriver
                    : [],
            );
        } catch (err) {
            logger.log(`Fetch error: ${err}`, "ERROR");
        } finally {
            setDriversLoading(false);
        }
    }, [userEmail]);

    const unassignedOrders = liveStats.unassigned;
    const inProgressOrders = liveStats.inProgress;

    const getPercent = (value, total) => {
        if (!total || total === 0) return 0;
        return ((value / total) * 100).toFixed(1);
    };

    const KPITile = ({ title, value, change, color, icon }) => (
        <div className={`kpi-tile kpi-${color}`}>
            <div className="kpi-header">
                <div className="kpi-title">{title}</div>
                <div className={`kpi-icon icon-${color}`}>{icon}</div>
            </div>
            <div className="kpi-value">{value}</div>
            <div className={`kpi-change ${color}`}>{change}</div>
        </div>
    );

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{
                    background: "#fff", padding: "12px 14px", borderRadius: "10px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)", fontSize: "13px"
                }}>
                    <p style={{ marginBottom: "6px", fontWeight: 600 }}>{label}</p>
                    <div style={{ color: "#3b82f6" }}>Created: {data.created}</div>
                    <div style={{ color: "#10b981" }}>Completed: {data.completed}</div>
                    <div style={{ color: "#ef4444" }}>Failed: {data.failed}</div>
                    <hr style={{ margin: "6px 0", border: "none", borderTop: "1px solid #eee" }} />
                    <div style={{ fontWeight: 600 }}>
                        Success: {data.created ? ((data.completed / data.created) * 100).toFixed(1) : 0}%
                    </div>
                </div>
            );
        }
        return null;
    };

    const getDelayHours = (delTime, delDate) => {
        if (!delTime || !delDate) return null;
        let normalizedDate = delDate;
        if (/^\d{2}-\d{2}-\d{4}$/.test(delDate)) {
            const [day, month, year] = delDate.split("-");
            normalizedDate = `${year}-${month}-${day}`;
        }
        if (/^\d{2}:\d{2}$/.test(delTime)) delTime = `${delTime}:00`;
        const deliveryDateTime = new Date(`${normalizedDate}T${delTime}`);
        if (isNaN(deliveryDateTime.getTime())) return null;
        return Math.abs(Math.floor((new Date() - deliveryDateTime) / (1000 * 60 * 60)));
    };

    const getDelayDuration = (delTime, delDate) => {
        if (!delTime || !delDate) return null;
        let normalizedDate = delDate;
        if (/^\d{2}-\d{2}-\d{4}$/.test(delDate)) {
            const [day, month, year] = delDate.split("-");
            normalizedDate = `${year}-${month}-${day}`;
        }
        if (/^\d{2}:\d{2}$/.test(delTime)) delTime = `${delTime}:00`;
        const deliveryDateTime = new Date(`${normalizedDate}T${delTime}`);
        if (isNaN(deliveryDateTime.getTime())) return null;
        const totalDays = Math.floor(Math.abs(new Date() - deliveryDateTime) / (1000 * 60 * 60 * 24));
        const months = Math.floor(totalDays / 30);
        const days = totalDays % 30;
        let result = "";
        if (months > 0) result += `${months} month${months > 1 ? "s" : ""} `;
        if (days > 0) result += `${days} day${days > 1 ? "s" : ""}`;
        return result.trim() || "0 days";
    };

    const isDelayed6h = (o) => { const h = getDelayHours(o.Del_Time, o.Del_Date); return h > 6 && h <= 12; };
    const isDelayed12h = (o) => { const h = getDelayHours(o.Del_Time, o.Del_Date); return h > 12 && h <= 24; };
    const isDelayed24h = (o) => { const h = getDelayHours(o.Del_Time, o.Del_Date); return h > 24; };

    const inactiveDriversCount = Array.isArray(tableDriver) ? tableDriver.filter((d) => d.status !== true).length : 0;
    const delayed6hCount = Array.isArray(orders) ? orders.filter(isDelayed6h).length : 0;
    const delayed12hCount = Array.isArray(orders) ? orders.filter(isDelayed12h).length : 0;
    const delayed24hCount = Array.isArray(orders) ? orders.filter(isDelayed24h).length : 0;

    const alertsData = [
        { label: "Orders delayed > 24 hrs", count: delayed24hCount, icon: <FiAlertTriangle />, color: "red" },
        { label: "Orders delayed > 12 hrs", count: delayed12hCount, icon: <FiClock />, color: "orange" },
        { label: "Orders delayed > 6 hrs", count: delayed6hCount, icon: <FiClock />, color: "yellow" },
        { label: "Inactive drivers", count: inactiveDriversCount, icon: <FiUserX />, color: "gray" },
    ];

    const fetchOrders = useCallback(async () => {
        setOrdersLoading(true);
        try {
            const res = await fetch(`/api/ordera/ordersAllData/${userEmail}`);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const data = await res.json();
            setOrders(data.data || []);
        } catch (err) {
            logger.log(`Orders fetch error: ${err}`, "ERROR");
        } finally {
            setOrdersLoading(false);
        }
    }, [userEmail]);

    const getChange = (today = 0, yesterday = 0) => {
        if (!yesterday && !today) return "No data";
        if (!yesterday) return "No data";
        const diff = today - yesterday;
        const percent = ((diff / yesterday) * 100).toFixed(1);
        return `${diff >= 0 ? "+" : ""}${percent}% vs yesterday`;
    };

    const formatAmount = (num) => {
        if (!num) return "0";
        if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
        if (num >= 100000) return (num / 100000).toFixed(2) + " L";
        if (num >= 1000) return (num / 1000).toFixed(1) + " K";
        return num.toString();
    };

    const fetchAllDriversTracking = useCallback(async () => {
        setTrackingLoading(true);
        try {
            const res = await fetch(`/api/dashboard/allDriversRoutes/${userEmail}`);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const data = await res.json();
            setAllDriverTracks(data.drivers || []);
        } catch (err) {
            logger.log(`Tracking fetch error: ${err}`, "ERROR");
        } finally {
            setTrackingLoading(false);
        }
    }, [userEmail]);

    const fetchDriverOrders = async (driver) => {
        setDriverModalLoading(true);
        try {
            const res = await fetch(`/api/DrOrder/order/${driver.email}`);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const data = await res.json();
            const active = Array.isArray(data)
                ? data.filter((o) => o.o_status !== "2" && o.o_status !== "3")
                : [];
            active.sort((a, b) => {
                const sa = a.stop_sequence ?? 9999;
                const sb = b.stop_sequence ?? 9999;
                if (sa !== sb) return sa - sb;
                return String(a.Order_No || "").localeCompare(String(b.Order_No || ""));
            });
            setDriverModalOrders(active);
        } catch (err) {
            logger.log(`Error fetching driver orders: ${err}`, "ERROR");
            setDriverModalOrders([]);
        } finally {
            setDriverModalLoading(false);
        }
    };

    const handleDriverClick = (driver) => {
        setSelectedDriverForModal(driver);
        setShowDriverOrdersModal(true);
        fetchDriverOrders(driver);
    };

    useEffect(() => {
        const loadInitialDashboard = async () => {
            setInitialLoading(true);
            await Promise.all([
                fetchData(),
                fetchKPIStats(),
                fetchTableData(),
                fetchOrders(),
            ]);
            setInitialLoading(false);
            // Map loads on its own — user sees KPIs/tables immediately
            fetchAllDriversTracking();
        };

        loadInitialDashboard();
    }, [userEmail]);

    useEffect(() => {

        fetchOverviewStats();

    }, [dateRange, fetchOverviewStats]);

    return (
        <>
            <Navbar />
            <TopProgressBar visible={initialLoading} />
            <div className="dashboard-container">
                <div className="dashboard-header">
                    <h2 className="dashboard-heading">
                        Dashboard

                        {initialLoading && (
                            <span style={{
                                marginLeft: 10,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 13,
                                color: "#94a3b8",
                                fontWeight: 400
                            }}>
                                <Spinner size={14} />
                                Loading…
                            </span>
                        )}
                    </h2>
                    <button
                        className="dashboard-toggle-button"
                        onClick={() => setShowData(prev => !prev)}
                    >
                        {showData ? "Hide Data" : "Show Data"}
                    </button>
                </div>

                {showData && (
                    <div className="kpi-grid">
                        {kpiLoading ? (
                            Array.from({ length: 6 }).map((_, i) => (
                                <KPISkeletonTile key={i} />
                            ))
                        ) : (
                            <>
                                <KPITile
                                    title="Total Orders"
                                    value={liveStats.total || 0}
                                    change={getChange(liveStats.today?.total, liveStats.yesterday?.total)}
                                    color="blue"
                                    icon={<FiShoppingCart />}
                                />

                                <KPITile
                                    title="Unassigned"
                                    value={unassignedOrders || 0}
                                    change={getChange(liveStats.today?.unassigned, liveStats.yesterday?.unassigned)}
                                    color="orange"
                                    icon={<FiPackage />}
                                />

                                <KPITile
                                    title="In Progress"
                                    value={inProgressOrders || 0}
                                    change={getChange(liveStats.today?.inProgress, liveStats.yesterday?.inProgress)}
                                    color="purple"
                                    icon={<FiTruck />}
                                />

                                <KPITile
                                    title="Completed"
                                    value={liveStats.completed || 0}
                                    change={getChange(liveStats.today?.completed, liveStats.yesterday?.completed)}
                                    color="green"
                                    icon={<FiCheckCircle />}
                                />

                                <KPITile
                                    title="Failed"
                                    value={liveStats.failed || 0}
                                    change={getChange(liveStats.today?.failed, liveStats.yesterday?.failed)}
                                    color="red"
                                    icon={<FiXCircle />}
                                />

                                <KPITile
                                    title="Active Drivers"
                                    value={liveStats.activeDrivers || 0}
                                    change={`Total: ${liveStats.totalDrivers || 0}`}
                                    color="blue"
                                    icon={<FiUsers />}
                                />
                            </>
                        )}
                    </div>
                )}

                <div className="dashboard-main">
                    <div className="right-panel" style={{ position: "relative" }}>
                        <div className="tracking-header" style={{ borderRadius: "14px 14px 0 0" }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Live Driver Tracking</h3>
                                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
                                    {allDriverTracks.length} driver{allDriverTracks.length !== 1 ? "s" : ""} on route
                                </p>
                            </div>
                            <div className="live-badge">LIVE</div>
                        </div>

                        <MapContainer
                            center={[22.9734, 78.6569]}
                            zoom={5}
                            style={{ height: "calc(100% - 58px)", width: "100%", borderRadius: "0 0 14px 14px" }}
                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; OpenStreetMap contributors'
                            />

                            {processedDriverTracks.length > 0 && <FitBounds tracks={processedDriverTracks} />}

                            {processedDriverTracks.map((driver, index) => {
                                const color = routeColors[index % routeColors.length];
                                const path = driver.snappedPath || [];
                                if (path.length === 0) return null;
                                const firstPos = path[0];
                                const lastPos = path[path.length - 1];

                                return (
                                    <React.Fragment key={driver.email}>
                                        <Polyline positions={path} pathOptions={{ color: "#000", weight: 5, opacity: 0.07 }} />
                                        <Polyline positions={path} pathOptions={{ color, weight: 4, opacity: 0.9 }} />

                                        <Marker position={firstPos} icon={getStartIcon("#10b981")}>
                                            <LeafletTooltip direction="top" offset={[0, -8]} opacity={1}>
                                                <div style={{ fontSize: 12 }}><strong>Start:</strong> {driver.name}</div>
                                            </LeafletTooltip>
                                        </Marker>

                                        <Marker position={lastPos} icon={getDriverIcon((driver.vehicle || "DEFAULT").toUpperCase(), color)}>
                                            <LeafletTooltip direction="top" offset={[0, -52]} opacity={1}>
                                                <div className="driver-card">
                                                    <div className="driver-plate" style={{ background: color }}>{driver.name}</div>
                                                    <div className="driver-meta">{driver.vehicle || "Driver"}</div>
                                                </div>
                                            </LeafletTooltip>
                                        </Marker>
                                    </React.Fragment>
                                );
                            })}
                        </MapContainer>

                        {(!trackingLoading ? processedDriverTracks.length < allDriverTracks.length : true) && (
                            <div style={{
                                position: "absolute",
                                top: 58,
                                left: 0, right: 0, bottom: 0,
                                zIndex: 500,
                                background: "rgba(255,255,255,0.75)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                gap: 8, fontSize: 13, color: "#64748b",
                                borderRadius: "0 0 14px 14px",
                                pointerEvents: "none",
                            }}>
                                <Spinner size={16} />
                                {trackingLoading ? "Fetching driver routes…" : "Snapping routes to roads…"}
                            </div>
                        )}

                        {processedDriverTracks.length > 0 && (
                            <div style={{
                                position: "absolute", bottom: 16, left: 16, zIndex: 400,
                                background: "rgba(255,255,255,0.95)", borderRadius: 10,
                                border: "2px solid rgba(0,0,0,0.2)", padding: "8px 12px",
                                boxShadow: "0 4px 14px rgba(0,0,0,0.12)", fontSize: 12,
                                maxWidth: 160, pointerEvents: "none"
                            }}>
                                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11, color: "#374151" }}>DRIVERS</div>
                                {processedDriverTracks.map((driver, index) => {
                                    const color = routeColors[index % routeColors.length];
                                    return (
                                        <div key={driver.email} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0, boxShadow: `0 0 0 2px ${color}44` }} />
                                            <span style={{ color: "#374151", fontWeight: 500 }}>{driver.name}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="left-panel">
                        <div className="order-overview">
                            <div className="overview-header">
                                <h3>Order Overview</h3>
                                <div className="date-filter">
                                    <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                                        <option value="today">Today</option>
                                        <option value="yesterday">Yesterday</option>
                                        <option value="7days">Last 7 Days</option>
                                        <option value="30days">Last 30 Days</option>
                                    </select>
                                </div>
                            </div>

                            <div className="funnel-row">
                                <div className="funnel-card blue">
                                    <span>
                                        {overviewStats.total}
                                    </span>
                                    <p>
                                        Received
                                    </p>
                                    <div className="funnel-icon blue">
                                        <FiShoppingCart />
                                    </div>
                                </div>
                                <div className="funnel-card orange">
                                    <span>
                                        {overviewStats.unassigned}
                                    </span>
                                    <p>
                                        Unassigned
                                    </p>
                                    <div className="funnel-icon orange">
                                        <FiPackage />
                                    </div>
                                </div>
                                <div className="funnel-card purple">
                                    <span>
                                        {overviewStats.inProgress}
                                    </span>
                                    <p>
                                        In Progress
                                    </p>
                                    <div className="funnel-icon purple">
                                        <FiTruck />
                                    </div>
                                </div>
                                <div className="funnel-card green">
                                    <span>
                                        {overviewStats.completed}
                                    </span>
                                    <p>
                                        Completed
                                    </p>
                                    <div className="funnel-icon green">
                                        <FiCheckCircle />
                                    </div>
                                </div>
                                <div className="funnel-card red">
                                    <span>
                                        {overviewStats.failed}
                                    </span>
                                    <p>
                                        Failed
                                    </p>
                                    <div className="funnel-icon red">
                                        <FiXCircle />
                                    </div>
                                </div>
                            </div>

                            <div className="progress-row">
                                {[
                                    { val: overviewStats.total, color: "#3b82f6" },
                                    { val: overviewStats.unassigned, color: "#f59e0b" },
                                    { val: overviewStats.inProgress, color: "#8b5cf6" },
                                    { val: overviewStats.completed, color: "#10b981" },
                                    { val: overviewStats.failed, color: "#ef4444" },
                                ].map((item, i) => {
                                    const percent = i === 0 ? 100 : getPercent(item.val, overviewStats.total);
                                    return (
                                        <div key={i}>
                                            <div className="progress-bar">
                                                <div className="progress-fill" style={{ width: `${percent}%`, background: item.color }} />
                                            </div>
                                            <small>{percent}%</small>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="metrics-row">
                                <div className="metric-box green">
                                    <div className="funnel-iconn green"><FiCheckCircle /></div>
                                    <div className="metric-content">
                                        <p>Success Rate</p>
                                        <strong>{liveStats.total ? getPercent(liveStats.completed, liveStats.total) : 0}%</strong>
                                    </div>
                                </div>
                                <div className="metric-box orange">
                                    <div className="funnel-iconn orange"><FiPackage /></div>
                                    <div className="metric-content"><p>Bills for Collection</p><strong>{liveStats.billsForCollection || 0}</strong></div>
                                </div>
                                <div className="metric-box highlight">
                                    <div className="funnel-iconn red">₹</div>
                                    <div className="metric-content"><p>Amount Pending</p><strong>₹ {formatAmount(liveStats.totalDueAmountForCollection || 0)}</strong></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bottom-grid">
                    <div className="alerts-box">
                        <div className="alerts-header"><h4>Alerts & Exceptions</h4></div>
                        {ordersLoading ? (
                            <SectionLoader height={160} label="Checking alerts…" />
                        ) : (
                            alertsData.map((item, i) => (
                                <React.Fragment key={i}>
                                    <div className="alert-item">
                                        <div className="alert-left">
                                            <span className={`alert-icon ${item.color}`}>{item.icon}</span>
                                            <p>{item.label}</p>
                                        </div>
                                        <strong>{item.count}</strong>
                                    </div>
                                    <hr />
                                </React.Fragment>
                            ))
                        )}
                    </div>

                    <div className="chart-box">
                        <div className="chart-header">
                            <h4>Orders Trend</h4>
                            <span>Past 7 Days</span>
                        </div>
                        {chartLoading ? (
                            <SectionLoader height={220} label="Loading trend data…" />
                        ) : trendData.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
                                📉 No orders found for last 7 days
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -30, bottom: 10 }}>
                                    <defs>
                                        <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                                        <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.4} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                                        <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} /><stop offset="95%" stopColor="#ef4444" stopOpacity={0} /></linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="2 4" opacity={0.3} />
                                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                    <Tooltip content={CustomTooltip} />
                                    <Area type="monotone" dataKey="created" stroke="#3b82f6" fill="url(#colorCreated)" strokeWidth={3} dot={false} />
                                    <Area type="monotone" dataKey="completed" stroke="#10b981" fill="url(#colorCompleted)" strokeWidth={3} dot={{ r: 3 }} />
                                    <Area type="monotone" dataKey="failed" stroke="#ef4444" fill="url(#colorFailed)" strokeWidth={2} dot={{ r: 3 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    <div className="drivers-box">
                        <div className="drivers-header">
                            <h4>Drivers Data</h4>
                            <span style={{ cursor: "pointer", color: "#3b82f6" }} onClick={() => setShowAllDrivers((prev) => !prev)}>
                                {showAllDrivers ? "Show Less" : "View All"}
                            </span>
                        </div>
                        {driversLoading ? (

                            <div
                                style={{
                                    padding: "10px 16px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 12
                                }}
                            >
                                {Array.from({ length: 5 }).map((_, i) => (

                                    <div
                                        key={i}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10
                                        }}
                                    >
                                        <Skeleton width={120} height={14} />

                                        <Skeleton width={40} height={14} />

                                        <Skeleton width={40} height={14} />

                                        <Skeleton width={40} height={14} />

                                        <Skeleton width={40} height={14} />

                                        <Skeleton width={50} height={14} />

                                    </div>
                                ))}
                            </div>

                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        <th>Driver</th>
                                        <th>Assigned</th>
                                        <th>Completed</th>
                                        <th>Failed</th>
                                        <th>POD</th>
                                        <th className="successTH">Success %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(Array.isArray(tableDriver) ? [...tableDriver] : [])
                                        .sort((a, b) => (tableCompletedOrdersMap.get(b.email) || 0) - (tableCompletedOrdersMap.get(a.email) || 0))
                                        .slice(0, showAllDrivers ? (Array.isArray(tableDriver) ? tableDriver.length : 0) : 5)
                                        .map((d, i) => {
                                            const completed = tableCompletedOrdersMap.get(d.email) || 0;
                                            const failed = tableFailedOrdersMap.get(d.email) || 0;
                                            const collectedPOD = tableCollectedPODMap.get(d.email) || 0;
                                            const activeAssigned = orders.filter((o) => {
                                                const orderEmail = String(o.Driv_Email || "").trim().toLowerCase();
                                                const driverEmail = String(d.email || "").trim().toLowerCase();
                                                return orderEmail === driverEmail && ["1", "4", "5"].includes(String(o.o_status));
                                            }).length;
                                            const total = activeAssigned + completed + failed;
                                            const success = total ? ((completed / total) * 100).toFixed(1) : 0;

                                            return (
                                                <tr key={i} className="dName--clickable" onClick={() => handleDriverClick(d)} title="Click to view active orders">
                                                    <td className="dName">{d.name}</td>
                                                    <td className="dNumber">{total}</td>
                                                    <td className="dNumber">{completed}</td>
                                                    <td className="dNumber">{failed}</td>
                                                    <td className="dNumber">₹ {collectedPOD}</td>
                                                    <td className="dNumber">{success}%</td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {showDriverOrdersModal && selectedDriverForModal && (
                <div className="driver-orders-overlay" onClick={() => setShowDriverOrdersModal(false)}>
                    <div className="driver-orders-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="driver-orders-modal__header">
                            <div className="driver-orders-modal__title-group">
                                <span className="active-drivers-dot green" style={{ marginRight: 8 }} />
                                <h3 className="driver-orders-modal__title">{selectedDriverForModal.name}</h3>
                            </div>
                            <button className="driver-orders-modal__close" onClick={() => setShowDriverOrdersModal(false)}>✕</button>
                        </div>

                        <div className="driver-orders-modal__subheader">
                            <span className="driver-orders-modal__badge">
                                {driverModalLoading
                                    ? "Loading orders…"
                                    : `${driverModalOrders.length} active order${driverModalOrders.length !== 1 ? "s" : ""}`}
                            </span>
                            {driverModalOrders.length > 1 && (
                                <Link
                                    to={`/RouteOptimization?driver=${encodeURIComponent(selectedDriverForModal.email)}`}
                                    className="dashboard-toggle-button driver-orders-modal__optimize-btn"
                                    onClick={() => setShowDriverOrdersModal(false)}
                                >
                                    Open Route Optimization →
                                </Link>
                            )}
                        </div>

                        <div className="driver-orders-modal__body">
                            {driverModalLoading ? (
                                <div className="driver-orders-modal__loader">
                                    <div className="driver-orders-modal__spinner" />
                                    <span>Loading orders…</span>
                                </div>
                            ) : driverModalOrders.length === 0 ? (
                                <div className="driver-orders-modal__empty">🎉 No active orders right now</div>
                            ) : (
                                driverModalOrders.map((order, idx) => {
                                    const statusMap = { "1": "Assigned", "4": "Picked Up", "5": "On the Way" };
                                    const statusClass = { "1": "status--assigned", "4": "status--picked", "5": "status--onway" };
                                    const delayHours = getDelayDuration(order.Del_Time, order.Del_Date);
                                    const isDelayed = delayHours !== null && new Date() > new Date(
                                        /^\d{2}-\d{2}-\d{4}$/.test(order.Del_Date)
                                            ? `${order.Del_Date.split("-").reverse().join("-")}T${order.Del_Time}`
                                            : `${order.Del_Date}T${order.Del_Time}`
                                    );

                                    return (
                                        <div key={order._id || idx} className="driver-order-card">
                                            <div className="driver-order-card__top">
                                                <strong className="driver-order-card__id">
                                                    {order.stop_sequence ? `#${order.stop_sequence} · ` : ""}{order.Order_No}
                                                </strong>
                                                <span className={`driver-order-card__status ${statusClass[order.o_status] || ""}`}>
                                                    {statusMap[order.o_status] || "In Progress"}
                                                </span>
                                            </div>
                                            <div className="driver-order-card__meta">
                                                <span className="driver-order-card__amount">₹{order.amount}</span>
                                                {delayHours !== null && (
                                                    <span className={`driver-order-card__delay ${isDelayed ? "delay--late" : "delay--ok"}`}>
                                                        {isDelayed ? `⚠ Delayed ${delayHours}` : `✓ Due in ${delayHours}`}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="driver-order-card__addresses">
                                                <div className="driver-order-card__addr-row">
                                                    <span className="addr-dot addr-dot--pickup" />
                                                    <span>{order.Rec_Name || "—"}</span>
                                                </div>
                                                <div className="driver-order-card__addr-row">
                                                    <span className="addr-dot addr-dot--drop" />
                                                    <span className="addr-text">{order.Rec_Add || "—"}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminDashboard;